// src/utils/ditteEsterne.js
// ═══════════════════════════════════════════════════════════════════════════
// DITTE ESTERNE — anagrafica unica e lavoro affidato (ottobre 2026, W405)
// ═══════════════════════════════════════════════════════════════════════════
// Le imprese esterne (subappaltatori) stanno in UNA sola anagrafica, la raccolta
// «subappaltatori»: con il piano Pro si usa l'anagrafica (commesse, costi, confronto), con il
// Gold la stessa ditta ha in più i documenti (pagina Documenti dei subappaltatori).
// Prima, senza Gold, l'impresa della commessa si scriveva a mano e anche il costo del
// subappalto era collegato al nome scritto: «Rossi Srl» e «Rossi s.r.l.» erano due ditte e
// non si poteva capire quanto lavoro si affida a chi.
// Qui le funzioni pure (provate a parte):
//  • nomeNorm: lo stesso nome scritto in modi diversi diventa uguale (per i dati vecchi);
//  • lavoroDitta: le commesse affidate a una ditta (dalla scheda della commessa) e i costi
//    di subappalto registrati (Controllo Economico), collegati per id o, per i dati vecchi,
//    per nome;
//  • confrontoDitte: per ogni ditta commesse, uomini-giorno, importi e quota sul totale.
// ═══════════════════════════════════════════════════════════════════════════

const FORME = [["societa a responsabilita limitata", "srl"], ["s r l s", "srls"], ["s r l", "srl"], ["s p a", "spa"], ["s n c", "snc"], ["s a s", "sas"]];

/** «Rossi Impianti S.r.l.» e «ROSSI IMPIANTI SRL» → «rossi impianti srl». */
export function nomeNorm(nome) {
  let s = String(nome || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, " ").trim();
  FORME.forEach(([da, a]) => { s = s.replace(new RegExp(`(^| )${da}( |$)`, "g"), `$1${a}$2`); });
  return s.replace(/\s+/g, " ").trim();
}

const isoDa = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v.slice(0, 10);
  const d = v.toDate ? v.toDate() : v instanceof Date ? v : null;
  return d ? d.toISOString().slice(0, 10) : "";
};
// «1.500,50» → 1500.5 · «1,5» e «1.5» → 1.5 · «1.500» → 1500 (W415: prima «1.5» diventava 15)
const num = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  let s = String(v ?? "").trim().replace(/\s/g, "");
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  else if (/^-?\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, "");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

/** Il costo di subappalto appartiene a questa ditta? (per id, o per nome nei dati vecchi) */
export function costoDellaDitta(m, ditta) {
  if (!m || m.categoriaId !== "subappaltatore" || m.tipo === "ricavo") return false;
  if (m.subappaltatoreId) return m.subappaltatoreId === ditta.id;
  return !!ditta.ragioneSociale && nomeNorm(m.dati?.nome) === nomeNorm(ditta.ragioneSociale);
}

/** L'impresa della commessa è questa ditta? (per id, o per nome scritto a mano) */
export function impresaDellaDitta(i, ditta) {
  if (!i) return false;
  if (i.subappaltatoreId) return i.subappaltatoreId === ditta.id;
  return !!ditta.ragioneSociale && nomeNorm(i.nome) === nomeNorm(ditta.ragioneSociale);
}

/**
 * Il lavoro affidato a una ditta: commesse (con lavorazioni e uomini-giorno presunti) e costi.
 * vedeImporti(project) dice se chi guarda può vedere gli importi di quella commessa.
 */
export function lavoroDitta(ditta, { progetti = [], movimenti = [], vedeImporti = () => true, dal = "", giorni = [] } = {}) {
  const commesse = [];
  // uomini-giorno reali dal Giornale di cantiere (W413), per commessa
  // (la stessa ditta scritta due volte nello stesso giorno conta un giorno solo; con «dal»
  // contano solo i giorni del periodo, come i costi)
  const reali = new Map();
  (giorni || []).filter(g => !dal || String(g.data || "") >= dal).forEach(g => dittePresenti(g).filter(x => voceDellaDitta(x, ditta)).forEach(x => {
    const r = reali.get(g.projectId) || { ug: 0, date: new Set(), ultimo: "" };
    r.ug += x.uomini; r.date.add(g.data); if (g.data > r.ultimo) r.ultimo = g.data;
    reali.set(g.projectId, r);
  }));
  progetti.filter(p => p && !p.isParent).forEach(p => {
    const imp = (p.impreseEsterne || []).filter(i => impresaDellaDitta(i, ditta));
    const costi = movimenti.filter(m => m.projectId === p.id && costoDellaDitta(m, ditta) && (!dal || (isoDa(m.dati?.data) || isoDa(m.creadaAt) || isoDa(m.createdAt) || "9999") >= dal));
    const re = reali.get(p.id);
    if (!imp.length && !costi.length && !re) return;
    const ug = imp.reduce((t, i) => t + (num(i.uominiGiorno) || 0), 0);
    const visibile = vedeImporti(p);
    commesse.push({
      project: p, lavorazioni: imp.map(i => i.lavorazioni).filter(Boolean).join("; "),
      uominiGiorno: ug || null, uominiGiornoReali: re ? Math.round(re.ug * 10) / 10 : null, giorniInCantiere: re ? re.date.size : 0, ultimoInCantiere: re?.ultimo || "",
      importo: visibile ? costi.reduce((t, m) => t + num(m.importo), 0) : null, nCosti: costi.length,
      inCorso: p.statoCommessa !== "chiusa",
    });
  });
  commesse.sort((a, b) => String(b.project.dataInizio || "").localeCompare(String(a.project.dataInizio || "")));
  const importiVisibili = commesse.filter(c => c.importo !== null);
  return {
    commesse,
    nCommesse: commesse.length, nInCorso: commesse.filter(c => c.inCorso).length,
    uominiGiorno: commesse.reduce((t, c) => t + (c.uominiGiorno || 0), 0),
    uominiGiornoReali: Math.round(commesse.reduce((t, c) => t + (c.uominiGiornoReali || 0), 0) * 10) / 10,
    importo: importiVisibili.reduce((t, c) => t + c.importo, 0),
    importiParziali: importiVisibili.length < commesse.length,   // qualche commessa non è visibile a chi guarda
  };
}

/** Commesse avviate da «dal» in poi (o ancora aperte): il periodo del confronto. */
const nelPeriodo = (p, dal) => !dal || p.statoCommessa !== "chiusa" || String(p.dataInizio || isoDa(p.createdAt) || "") >= dal;

/** Confronto tra ditte: righe ordinate per importo (poi commesse), con le quote sul totale. */
export function confrontoDitte(ditte = [], { progetti = [], movimenti = [], vedeImporti = () => true, dal = "", giorni = [] } = {}) {
  const prog = progetti.filter(p => nelPeriodo(p, dal));
  const righe = ditte.filter(d => d && d.attivo !== false).map(d => ({ ditta: d, ...lavoroDitta(d, { progetti: prog, movimenti, vedeImporti, giorni, dal }) }));
  const totImporto = righe.reduce((t, r) => t + r.importo, 0);
  const totCommesse = righe.reduce((t, r) => t + r.nCommesse, 0);
  return righe.map(r => ({
    ...r,
    quotaImporto: totImporto > 0 ? Math.round(r.importo / totImporto * 1000) / 10 : null,
    quotaCommesse: totCommesse > 0 ? Math.round(r.nCommesse / totCommesse * 1000) / 10 : null,
  })).sort((a, b) => b.importo - a.importo || b.nCommesse - a.nCommesse || String(a.ditta.ragioneSociale || "").localeCompare(String(b.ditta.ragioneSociale || "")));
}

/** Nomi scritti a mano (commesse e costi) che non corrispondono a nessuna ditta in anagrafica. */
export function nomiNonInAnagrafica(ditte = [], { progetti = [], movimenti = [] } = {}) {
  const noti = new Set(ditte.map(d => nomeNorm(d.ragioneSociale)).filter(Boolean));
  const visti = new Map();
  const metti = (nome) => { const k = nomeNorm(nome); if (k && !noti.has(k) && !visti.has(k)) visti.set(k, String(nome).trim()); };
  progetti.forEach(p => (p.impreseEsterne || []).filter(i => !i.subappaltatoreId).forEach(i => metti(i.nome)));
  movimenti.filter(m => m.categoriaId === "subappaltatore" && !m.subappaltatoreId).forEach(m => metti(m.dati?.nome));
  return [...visti.values()].sort((a, b) => a.localeCompare(b));
}

// ═══════════════════════════════════════════════════════════════════════════
// PRESENZE IN CANTIERE E DOCUMENTI DELLE DITTE (ottobre 2026, W413)
// ═══════════════════════════════════════════════════════════════════════════
//  • nel Giornale di cantiere le ditte presenti si scelgono dall'anagrafica, con quanti uomini
//    (campo «ditteEsterne» del giorno; il vecchio testo «subappaltatori» resta com'era);
//  • dagli stessi giorni: uomini-giorno REALI per ditta e commessa, da confrontare con i
//    presunti scritti nella commessa;
//  • con il piano Gold, la ditta con documenti scaduti o mancanti viene segnalata (si
//    considerano solo le ditte di cui l'azienda gestisce già i documenti).

/** Le ditte presenti in un giorno del Giornale di cantiere. */
export function dittePresenti(giorno) {
  return (giorno?.ditteEsterne || [])
    .filter(x => x && (x.subappaltatoreId || String(x.nome || "").trim()))
    .map(x => ({ subappaltatoreId: x.subappaltatoreId || "", nome: String(x.nome || "").trim(), uomini: Math.max(0, num(x.uomini)), lavorazioni: String(x.lavorazioni || "").trim() }));
}

/** Chiave della ditta: l'id se c'è, altrimenti il nome normalizzato. */
export const chiaveDitta = (x) => (x?.subappaltatoreId ? `id:${x.subappaltatoreId}` : `n:${nomeNorm(x?.nome)}`);

/** La voce (impresa della commessa, presenza nel giornale) è di questa ditta? */
export function voceDellaDitta(v, ditta) {
  if (!v || !ditta) return false;
  if (v.subappaltatoreId && ditta.id) return v.subappaltatoreId === ditta.id;
  return !!ditta.ragioneSociale && nomeNorm(v.nome) === nomeNorm(ditta.ragioneSociale);
}

/**
 * Uomini-giorno reali dal Giornale: per ditta e commessa.
 * → [{ chiave, subappaltatoreId, nome, projectId, uominiGiorno, giorni, primo, ultimo }]
 */
export function presenzeDalGiornale(giorni = []) {
  const m = new Map();
  (giorni || []).forEach(g => dittePresenti(g).forEach(x => {
    const k = `${chiaveDitta(x)}|${g.projectId || ""}`;
    const r = m.get(k) || { chiave: chiaveDitta(x), subappaltatoreId: x.subappaltatoreId, nome: x.nome, projectId: g.projectId || "", uominiGiorno: 0, giorni: 0, date: new Set(), primo: "", ultimo: "" };
    r.uominiGiorno += x.uomini; r.date.add(g.data || ""); r.giorni = r.date.size; // un giorno conta una volta
    if (!r.primo || g.data < r.primo) r.primo = g.data;
    if (!r.ultimo || g.data > r.ultimo) r.ultimo = g.data;
    if (x.nome) r.nome = x.nome;
    m.set(k, r);
  }));
  return [...m.values()].map(({ date, ...r }) => r);
}

/**
 * Le ditte di una commessa, tutto insieme: lavorazioni e uomini-giorno presunti (commessa),
 * uomini-giorno reali e giorni in cantiere (giornale), costi di subappalto (Controllo Economico).
 * vedeImporti false → costo null.
 */
export function ditteDellaCommessa(project, { giorni = [], movimenti = [], vedeImporti = true } = {}) {
  // ogni ditta una riga sola: «chiavi» porta id e nome alla stessa riga (W415: prima la stessa
  // ditta trovata una volta per nome e una per id compariva due volte)
  const elenco = [];
  const chiavi = new Map();
  const prendi = (x) => {
    const k = chiaveDitta(x);
    const kNome = nomeNorm(x.nome) ? `n:${nomeNorm(x.nome)}` : "";
    let r = chiavi.get(k) || (kNome && chiavi.get(kNome));
    // stessa ditta con un altro id ma con lo stesso nome: si riunisce solo se una delle due non ha id
    if (r && x.subappaltatoreId && r.subappaltatoreId && r.subappaltatoreId !== x.subappaltatoreId) r = null;
    if (!r) {
      r = { subappaltatoreId: x.subappaltatoreId || "", nome: x.nome || "", lavorazioni: [], presunti: 0, reali: 0, giorni: 0, primo: "", ultimo: "", costo: vedeImporti ? 0 : null, nCosti: 0 };
      elenco.push(r);
    }
    if (x.subappaltatoreId && !r.subappaltatoreId) r.subappaltatoreId = x.subappaltatoreId;
    if (!r.nome && x.nome) r.nome = x.nome;
    chiavi.set(k, r);
    if (r.subappaltatoreId) chiavi.set(`id:${r.subappaltatoreId}`, r);
    if (kNome && !chiavi.has(kNome)) chiavi.set(kNome, r);
    return r;
  };
  (project?.impreseEsterne || []).filter(i => i && (i.subappaltatoreId || String(i.nome || "").trim())).forEach(i => {
    const r = prendi(i);
    if (i.lavorazioni) r.lavorazioni.push(i.lavorazioni);
    r.presunti += num(i.uominiGiorno) || 0;
  });
  presenzeDalGiornale((giorni || []).filter(g => !project?.id || !g.projectId || g.projectId === project.id)).forEach(p => {
    const r = prendi(p);
    r.reali += p.uominiGiorno; r.giorni += p.giorni;
    if (!r.primo || p.primo < r.primo) r.primo = p.primo;
    if (!r.ultimo || p.ultimo > r.ultimo) r.ultimo = p.ultimo;
  });
  (movimenti || []).filter(m => m && m.categoriaId === "subappaltatore" && m.tipo !== "ricavo" && (!project?.id || m.projectId === project.id)).forEach(m => {
    const nome = m.dati?.nome || "";
    if (!m.subappaltatoreId && !nomeNorm(nome)) return;
    const r = prendi({ subappaltatoreId: m.subappaltatoreId || "", nome });
    r.nCosti += 1;
    if (vedeImporti) r.costo += num(m.importo);
  });
  return elenco.map(r => ({
    ...r, lavorazioni: r.lavorazioni.join("; "),
    presunti: Math.round(r.presunti * 10) / 10, reali: Math.round(r.reali * 10) / 10,
    oltre: r.presunti > 0 && r.reali > r.presunti,
  })).sort((a, b) => b.reali - a.reali || b.presunti - a.presunti || a.nome.localeCompare(b.nome));
}

/** «12 uomini-giorno» / «1 uomo-giorno» */
export const testoUG = (n) => `${String(Math.round((n || 0) * 10) / 10).replace(".", ",")} ${Math.round((n || 0) * 10) / 10 === 1 ? "uomo-giorno" : "uomini-giorno"}`;

/**
 * Sezione «Ditte esterne» della Relazione della commessa (W413): chi ha lavorato, cosa ha
 * fatto, uomini-giorno reali e previsti, costo del subappalto. null se non ci sono ditte.
 * → { testo, righe: [testo per ditta], attenzione: [frasi per gli elementi di attenzione] }
 */
export function sezioneDitteRelazione(righe = [], { fmtEuro = (n) => `€ ${Math.round(n).toLocaleString("it-IT")}`, fmtData = (s) => (s ? s.split("-").reverse().join("/") : "") } = {}) {
  if (!righe.length) return null;
  const testo = righe.length === 1 ? "Alla commessa ha lavorato un'impresa esterna." : `Alla commessa hanno lavorato ${righe.length} imprese esterne.`;
  const per = righe.map(r => {
    const parti = [];
    if (r.reali > 0) parti.push(`${testoUG(r.reali)} in ${r.giorni} giorn${r.giorni === 1 ? "o" : "i"} di cantiere${r.primo ? (r.primo === r.ultimo ? ` (il ${fmtData(r.primo)})` : ` (dal ${fmtData(r.primo)} al ${fmtData(r.ultimo)})`) : ""}`);
    else parti.push("non risulta ancora nel Giornale di cantiere");
    if (r.presunti > 0) parti.push(`${testoUG(r.presunti)} previsti`);
    if (r.costo !== null && r.nCosti) parti.push(`subappalto registrato ${fmtEuro(r.costo)}`);
    return `${r.nome}${r.lavorazioni ? ` (${r.lavorazioni})` : ""}: ${parti.join("; ")}.`;
  });
  const attenzione = righe.filter(r => r.oltre).map(r => `${r.nome}: ${testoUG(r.reali)} dal Giornale di cantiere, oltre i ${testoUG(r.presunti)} previsti.`);
  return { testo, righe: per, attenzione };
}
